import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { Plus, CheckCircle, AlertTriangle, XCircle, Trash2, Loader, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';

const SEVERITY_OPTIONS = [
  { value: 'minor',    label: '🟡 Minor',    desc: 'Small degradation, most users unaffected' },
  { value: 'major',    label: '🟠 Major',    desc: 'Significant impact, many users affected' },
  { value: 'critical', label: '🔴 Critical', desc: 'Full outage, all users affected' },
];

const COMPONENT_OPTIONS = [
  { value: 'general',  label: 'General' },
  { value: 'servers',  label: 'Game Servers' },
  { value: 'panel',    label: 'Control Panel' },
  { value: 'billing',  label: 'Billing System' },
  { value: 'network',  label: 'Network' },
  { value: 'support',  label: 'Support System' },
];

const SEVERITY_STYLES = {
  minor:    'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  major:    'bg-orange-500/10 text-orange-400 border-orange-500/20',
  critical: 'bg-red-500/10 text-red-400 border-red-500/20',
};

const IncidentManager = () => {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    title: '',
    message: '',
    severity: 'minor',
    affected_component: 'general',
  });

  useEffect(() => {
    api.get('/status/incidents')
      .then(({ data }) => setIncidents(data.incidents))
      .catch(() => toast.error('Failed to load incidents'))
      .finally(() => setLoading(false));
  }, []);

  const createIncident = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.message.trim()) {
      toast.error('Title and message are required');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post('/status/incidents', form);
      setIncidents(prev => [data.incident, ...prev]);
      setForm({ title: '', message: '', severity: 'minor', affected_component: 'general' });
      setShowForm(false);
      toast.success('Incident created — status page updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create incident');
    } finally {
      setSubmitting(false);
    }
  };

  const resolveIncident = async (id) => {
    try {
      const { data } = await api.patch(`/status/incidents/${id}`, { resolved: true });
      setIncidents(prev => prev.map(i => i.id === id ? data.incident : i));
      toast.success('Incident resolved');
    } catch {
      toast.error('Failed to resolve incident');
    }
  };

  const deleteIncident = async (id) => {
    if (!window.confirm('Delete this incident?')) return;
    try {
      await api.delete(`/status/incidents/${id}`);
      setIncidents(prev => prev.filter(i => i.id !== id));
      toast.success('Incident deleted');
    } catch {
      toast.error('Failed to delete incident');
    }
  };

  const activeIncidents = incidents.filter(i => !i.resolved_at);
  const resolvedIncidents = incidents.filter(i => i.resolved_at);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold text-white text-lg">Status Page Management</h2>
          <p className="text-gray-500 text-sm mt-0.5">Incidents are shown publicly at /status</p>
        </div>
        <div className="flex items-center gap-3">
          <a
            href="/status"
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white transition-colors"
          >
            <ExternalLink className="w-4 h-4" /> View Status Page
          </a>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 text-red-400 px-4 py-2 rounded-xl text-sm transition-colors"
          >
            <Plus className="w-4 h-4" /> Report Incident
          </button>
        </div>
      </div>

      {/* Create incident form */}
      {showForm && (
        <div className="bg-gray-800/50 border border-red-500/20 rounded-2xl p-6">
          <h3 className="font-bold text-white mb-4">Report New Incident</h3>
          <form onSubmit={createIncident} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Title</label>
              <input
                type="text"
                required
                value={form.title}
                onChange={e => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Elevated latency on EU nodes"
                className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Message</label>
              <textarea
                required
                rows={3}
                value={form.message}
                onChange={e => setForm({ ...form, message: e.target.value })}
                placeholder="Describe the issue and what you're doing to fix it..."
                className="w-full bg-gray-800 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-red-500 transition-colors resize-none"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Severity</label>
                <div className="space-y-2">
                  {SEVERITY_OPTIONS.map(opt => (
                    <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                      form.severity === opt.value
                        ? SEVERITY_STYLES[opt.value]
                        : 'border-gray-700 hover:border-gray-600'
                    }`}>
                      <input
                        type="radio"
                        name="severity"
                        value={opt.value}
                        checked={form.severity === opt.value}
                        onChange={e => setForm({ ...form, severity: e.target.value })}
                        className="hidden"
                      />
                      <div>
                        <div className="text-sm font-medium text-white">{opt.label}</div>
                        <div className="text-xs text-gray-500">{opt.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1.5">Affected Component</label>
                <div className="space-y-1.5">
                  {COMPONENT_OPTIONS.map(opt => (
                    <label key={opt.value} className={`flex items-center gap-2 p-2.5 rounded-xl border cursor-pointer transition-colors text-sm ${
                      form.affected_component === opt.value
                        ? 'border-gray-500 bg-gray-700 text-white'
                        : 'border-gray-700 text-gray-400 hover:border-gray-600'
                    }`}>
                      <input
                        type="radio"
                        name="affected_component"
                        value={opt.value}
                        checked={form.affected_component === opt.value}
                        onChange={e => setForm({ ...form, affected_component: e.target.value })}
                        className="hidden"
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white font-bold px-5 py-2.5 rounded-xl text-sm transition-colors"
              >
                {submitting ? <Loader className="w-4 h-4 animate-spin" /> : <AlertTriangle className="w-4 h-4" />}
                Post Incident
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="px-5 py-2.5 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-xl text-sm transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Active incidents */}
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader className="w-6 h-6 text-green-400 animate-spin" />
        </div>
      ) : (
        <>
          {activeIncidents.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-3">
                🔴 Active Incidents ({activeIncidents.length})
              </h3>
              <div className="space-y-3">
                {activeIncidents.map(incident => (
                  <IncidentRow
                    key={incident.id}
                    incident={incident}
                    onResolve={resolveIncident}
                    onDelete={deleteIncident}
                  />
                ))}
              </div>
            </div>
          )}

          {activeIncidents.length === 0 && (
            <div className="flex items-center gap-3 bg-green-500/10 border border-green-500/20 rounded-2xl p-5">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
              <div>
                <div className="font-semibold text-green-400">All systems operational</div>
                <div className="text-gray-400 text-sm">No active incidents. Status page shows green.</div>
              </div>
            </div>
          )}

          {resolvedIncidents.length > 0 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                Past Incidents
              </h3>
              <div className="space-y-2 opacity-60">
                {resolvedIncidents.slice(0, 5).map(incident => (
                  <IncidentRow
                    key={incident.id}
                    incident={incident}
                    onDelete={deleteIncident}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const IncidentRow = ({ incident, onResolve, onDelete }) => {
  const isResolved = !!incident.resolved_at;
  const sev = SEVERITY_STYLES[incident.severity] || SEVERITY_STYLES.minor;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-semibold text-white text-sm">{incident.title}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full border ${sev}`}>
              {incident.severity}
            </span>
            <span className="text-xs text-gray-500">{incident.affected_component}</span>
          </div>
          <p className="text-gray-400 text-xs leading-relaxed mb-2">{incident.message}</p>
          <div className="text-gray-600 text-xs">
            {new Date(incident.created_at).toLocaleString()}
            {isResolved && ` → Resolved ${new Date(incident.resolved_at).toLocaleString()}`}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {!isResolved && onResolve && (
            <button
              onClick={() => onResolve(incident.id)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-500/10 hover:bg-green-500/20 border border-green-500/20 text-green-400 text-xs rounded-lg transition-colors"
            >
              <CheckCircle className="w-3.5 h-3.5" /> Resolve
            </button>
          )}
          <button
            onClick={() => onDelete(incident.id)}
            className="p-1.5 bg-gray-800 hover:bg-red-500/10 hover:text-red-400 border border-gray-700 text-gray-400 rounded-lg transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default IncidentManager;
